import React, { useState, useEffect, useCallback } from 'react';
import { gamificationApi, peopleApi } from '../api';

interface Task {
  id: number;
  title: string;
  description: string;
  task_type: string;
  target_person_id: number | null;
  target_person_name: string | null;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  due_date: string | null;
  reminder_date: string | null;
  exp_reward: number;
  status: string;
  completed_at: string | null;
  created_at: string;
}

interface Person {
  id: number;
  name: string;
}

interface TaskListProps {
  onTaskComplete?: () => void;
}

const TaskList: React.FC<TaskListProps> = ({ onTaskComplete }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('pending');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'contact',
    target_person_id: '',
    is_recurring: false,
    recurrence_pattern: '',
    due_date: '',
    exp_reward: 10,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, peopleRes] = await Promise.all([
        gamificationApi.getTasks(filter === 'all' ? undefined : filter),
        peopleApi.getAll(),
      ]);
      setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
      setPeople(Array.isArray(peopleRes.data) ? peopleRes.data : []);
    } catch (error) {
      console.error('加载任务失败:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await gamificationApi.createTask({
        ...formData,
        target_person_id: formData.target_person_id ? parseInt(formData.target_person_id) : undefined,
        due_date: formData.due_date || undefined,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : undefined,
      });
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        task_type: 'contact',
        target_person_id: '',
        is_recurring: false,
        recurrence_pattern: '',
        due_date: '',
        exp_reward: 10,
      });
      loadData();
    } catch (error) {
      console.error('创建任务失败:', error);
      alert('创建任务失败');
    }
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      const result = await gamificationApi.completeTask(taskId);
      alert(`🎉 任务完成！获得 ${result.data.exp_gained} 经验值`);
      loadData();
      onTaskComplete?.();
    } catch (error) {
      console.error('完成任务失败:', error);
      alert('完成任务失败');
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('确定要删除这个任务吗？')) return;
    try {
      await gamificationApi.deleteTask(taskId);
      loadData();
    } catch (error) {
      console.error('删除任务失败:', error);
      alert('删除任务失败');
    }
  };

  const getTaskTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'contact': '联系',
      'diary': '写日记',
      'analysis': '分析',
      'custom': '自定义',
    };
    return labels[type] || type;
  };

  const getTaskTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      'contact': '📞',
      'diary': '📝',
      'analysis': '🔍',
      'custom': '📌',
    };
    return icons[type] || '📌';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'completed': return '#4caf50';
      case 'overdue': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '待完成';
      case 'completed': return '已完成';
      case 'overdue': return '已过期';
      case 'cancelled': return '已取消';
      default: return status;
    }
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'completed') return false;
    return new Date(task.due_date) < new Date();
  };

  if (loading) {
    return <div className="loading">加载任务中...</div>;
  }

  return (
    <div className="task-list">
      {/* 工具栏 */}
      <div className="toolbar">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            待完成
          </button>
          <button
            className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            已完成
          </button>
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            全部
          </button>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + 新建任务
        </button>
      </div>

      {/* 新建任务表单 */}
      {showForm && (
        <div className="task-form-overlay">
          <div className="task-form">
            <h3>新建任务</h3>
            <form onSubmit={handleCreateTask}>
              <div className="form-group">
                <label>任务标题 *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="例如：联系张三"
                  required
                />
              </div>

              <div className="form-group">
                <label>任务描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="任务的详细描述..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>任务类型</label>
                  <select
                    value={formData.task_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, task_type: e.target.value }))}
                  >
                    <option value="contact">联系某人</option>
                    <option value="diary">写日记</option>
                    <option value="analysis">性格分析</option>
                    <option value="custom">自定义</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>关联人员</label>
                  <select
                    value={formData.target_person_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_person_id: e.target.value }))}
                  >
                    <option value="">不关联</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>截止日期</label>
                  <input
                    type="datetime-local"
                    value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>经验值奖励</label>
                  <input
                    type="number"
                    value={formData.exp_reward}
                  onChange={(e) => setFormData(prev => ({ ...prev, exp_reward: parseInt(e.target.value) || 10 }))}
                    min={1}
                    max={100}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_recurring: e.target.checked }))}
                  />
                  重复任务
                </label>
              </div>

              {formData.is_recurring && (
                <div className="form-group">
                  <label>重复周期</label>
                  <select
                    value={formData.recurrence_pattern}
                    onChange={(e) => setFormData(prev => ({ ...prev, recurrence_pattern: e.target.value }))}
                  >
                    <option value="">选择周期</option>
                    <option value="daily">每天</option>
                    <option value="weekly">每周</option>
                    <option value="monthly">每月</option>
                  </select>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  创建任务
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 任务列表 */}
      <div className="tasks">
        {tasks.length === 0 ? (
          <div className="empty-state">
            <p>暂无任务</p>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              创建第一个任务
            </button>
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              className={`task-card ${task.status} ${isOverdue(task) ? 'overdue' : ''}`}
            >
              <div className="task-icon">{getTaskTypeIcon(task.task_type)}</div>
              <div className="task-content">
                <div className="task-header">
                  <span className="task-title">{task.title}</span>
                  <span
                    className="task-status"
                    style={{ backgroundColor: getStatusColor(isOverdue(task) ? 'overdue' : task.status) }}
                  >
                    {getStatusLabel(isOverdue(task) ? 'overdue' : task.status)}
                  </span>
                </div>
                {task.description && <p className="task-desc">{task.description}</p>}
                <div className="task-meta">
                  <span className="task-type">{getTaskTypeLabel(task.task_type)}</span>
                  {task.target_person_name && (
                    <span className="task-person">👤 {task.target_person_name}</span>
                  )}
                  {task.due_date && (
                    <span className="task-due">
                      📅 {new Date(task.due_date).toLocaleString('zh-CN')}
                    </span>
                  )}
                  {task.is_recurring && (
                    <span className="task-recurring">🔄 重复</span>
                  )}
                  <span className="task-exp">🎯 {task.exp_reward} EXP</span>
                </div>
              </div>
              <div className="task-actions">
                {task.status === 'pending' && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleCompleteTask(task.id)}
                  >
                    ✓ 完成
                  </button>
                )}
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteTask(task.id)}
                >
                  删除
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <style>{`
        .task-list {
          padding: 10px 0;
        }

        .toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .filter-tabs {
          display: flex;
          gap: 5px;
        }

        .filter-tab {
          padding: 8px 16px;
          border: none;
          background: #f5f5f5;
          cursor: pointer;
          border-radius: 20px;
          font-size: 14px;
          transition: all 0.2s;
        }

        .filter-tab:hover {
          background: #e0e0e0;
        }

        .filter-tab.active {
          background: #667eea;
          color: white;
        }

        .task-form-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .task-form {
          background: white;
          padding: 25px;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .task-form h3 {
          margin: 0 0 20px 0;
          color: #333;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-size: 14px;
          color: #666;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }

        .checkbox-label {
          display: flex !important;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .checkbox-label input {
          width: auto !important;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
        }

        .tasks {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .task-card {
          display: flex;
          gap: 15px;
          padding: 15px;
          background: white;
          border: 1px solid #eee;
          border-radius: 10px;
          transition: all 0.2s;
        }

        .task-card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .task-card.completed {
          opacity: 0.7;
          background: #f9f9f9;
        }

        .task-card.overdue {
          border-color: #f44336;
          background: #fff5f5;
        }

        .task-icon {
          font-size: 28px;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f5f5;
          border-radius: 10px;
          flex-shrink: 0;
        }

        .task-content {
          flex: 1;
          min-width: 0;
        }

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 5px;
        }

        .task-title {
          font-weight: 600;
          font-size: 16px;
          color: #333;
        }

        .task-status {
          font-size: 11px;
          padding: 3px 10px;
          border-radius: 10px;
          color: white;
        }

        .task-desc {
          font-size: 13px;
          color: #666;
          margin: 0 0 8px 0;
        }

        .task-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          font-size: 12px;
          color: #888;
        }

        .task-meta span {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .task-exp {
          color: #ff9800 !important;
          font-weight: 500;
        }

        .task-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 12px;
        }

        .btn-success {
          background: #4caf50;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .btn-success:hover {
          background: #43a047;
        }

        .btn-danger {
          background: #f44336;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .btn-danger:hover {
          background: #e53935;
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: #999;
        }

        .empty-state p {
          margin-bottom: 15px;
        }

        @media (max-width: 600px) {
          .toolbar {
            flex-direction: column;
            gap: 15px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .task-card {
            flex-direction: column;
          }

          .task-actions {
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default TaskList;
